import React, { useEffect, useMemo, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { X, Search } from 'lucide-react';
import { API_URL } from '../../../api';
import { joinProjectLabel } from '../../../utils/projectLabel';
import TestProjektBadge, { arTestprojekt } from '../../TestProjektBadge';

function fetchProjects() {
  let tenantSlug = '';
  try { tenantSlug = String(localStorage.getItem('kanplan-active-tenant-slug') || '').trim(); } catch {}
  return fetch(`${API_URL}/projects/`, {
    credentials: 'include',
    headers: tenantSlug ? { 'X-Tenant-Slug': tenantSlug } : {},
  }).then((r) => (r.ok ? r.json() : []));
}

export function ProjectSwitcherModal({ onClose, onSelect, titel = 'Byt projekt' }) {
  const [projects, setProjects] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [query, setQuery] = useState('');
  const inputRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    fetchProjects()
      .then((data) => {
        if (cancelled) return;
        setProjects(Array.isArray(data) ? data : []);
        setError(null);
      })
      .catch(() => {
        if (cancelled) return;
        setError('Kunde inte hämta projektlista.');
        setProjects([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => { cancelled = true; };
  }, []);

  useEffect(() => {
    inputRef.current?.focus();
  }, []);

  useEffect(() => {
    function onKey(e) {
      if (e.key === 'Escape') onClose();
    }
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [onClose]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    // Alla projekt visas (listan rullar); tidigare bara de 30 första, så
    // projekt längre ned gick bara att nå via sök (Mikael F5 2026-10-15).
    // "test" i sökningen hittar testprojekten (TEST-etiketten).
    if (!q) return projects;
    return projects.filter((p) => {
      const haystack = [p?.name, p?.title, p?.number, p?.project_number, p?.client_name, arTestprojekt(p) ? 'test' : '']
        .filter(Boolean)
        .join(' ')
        .toLowerCase();
      return haystack.includes(q);
    });
  }, [query, projects]);

  return ReactDOM.createPortal(
    <div
      className="kp-project-switcher-overlay"
      role="dialog"
      aria-modal="true"
      aria-labelledby="kp-project-switcher-title"
    >
      <div className="kp-project-switcher-backdrop" onClick={onClose} aria-hidden="true" />
      <div className="kp-project-switcher">
        <div className="kp-project-switcher-header">
          <h2 id="kp-project-switcher-title">{titel}</h2>
          <button type="button" className="kp-project-switcher-close" onClick={onClose} aria-label="Stäng">
            <X size={15} strokeWidth={2.5} aria-hidden="true" />
          </button>
        </div>
        <div className="kp-project-switcher-search">
          <Search size={14} strokeWidth={2} aria-hidden="true" className="kp-project-switcher-search-icon" />
          <input
            ref={inputRef}
            type="text"
            placeholder="Sök på namn, nummer eller kund…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            autoComplete="off"
            spellCheck="false"
            aria-label="Sök bland projekt"
          />
        </div>
        {!loading && !error && projects.length > 0 ? (
          <div className="kp-project-switcher-count" aria-live="polite">
            {query.trim() ? `${filtered.length} av ${projects.length} projekt` : `${projects.length} projekt`}
          </div>
        ) : null}
        <div className="kp-project-switcher-body">
          {loading ? (
            <div className="kp-project-switcher-empty">Hämtar projekt…</div>
          ) : error ? (
            <div className="kp-project-switcher-empty kp-project-switcher-empty--error">{error}</div>
          ) : filtered.length === 0 ? (
            <div className="kp-project-switcher-empty">Inga projekt matchade din sökning.</div>
          ) : (
            <ul className="kp-project-switcher-list">
              {filtered.map((p) => {
                const id = String(p.id);
                const name = joinProjectLabel(p.number || p.project_number, p.name || p.title) || `Projekt #${id}`;
                const subtitle = [p.status, p.client_name]
                  .filter(Boolean)
                  .join(' · ');
                const test = arTestprojekt(p);
                return (
                  <li key={id}>
                    <button
                      type="button"
                      className="kp-project-switcher-item"
                      onClick={() => onSelect({ id, name, number: p.number || p.project_number, status: p.status, hogia_linked: p.hogia_linked })}
                    >
                      <span className="kp-project-switcher-item-name">{name}</span>
                      {subtitle || test ? (
                        <span className="kp-project-switcher-item-meta">
                          {test ? <TestProjektBadge /> : null}
                          {test && subtitle ? ' ' : null}
                          {subtitle}
                        </span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
            </ul>
          )}
        </div>
      </div>
    </div>,
    document.body
  );
}
